import { createContext, type ReactNode, useContext } from "react";

/**
 * Router-agnostic routing surface for the workflows editor.
 *
 * The editor core never imports a router directly; instead each host
 * (track-office on TanStack Router, baback-office on react-router-dom)
 * provides the current shopId and navigation hooks through this context —
 * same pattern as the templates editor's TemplatesEditorRoutingContext.
 */
export type WorkflowsEditorRouting = {
  shopId: string;

  /** Open a template's editor page (email node "customize" button). */
  openTemplate?: (templateId: string) => void;

  /**
   * Host-provided hook into internal router navigation, used to warn about
   * unsaved changes. `shouldBlock` returns the confirmation message while
   * the editor is dirty. Returns an unsubscribe function. Hosts that don't
   * provide it only get the browser `beforeunload` guard.
   */
  registerNavigationGuard?: (shouldBlock: () => string) => () => void;
};

const WorkflowsEditorRoutingContext = createContext<
  WorkflowsEditorRouting | undefined
>(undefined);

export function WorkflowsEditorRoutingProvider({
  value,
  children,
}: {
  value: WorkflowsEditorRouting;
  children: ReactNode;
}) {
  return (
    <WorkflowsEditorRoutingContext.Provider value={value}>
      {children}
    </WorkflowsEditorRoutingContext.Provider>
  );
}

export function useWorkflowsEditorRouting(): WorkflowsEditorRouting {
  const context = useContext(WorkflowsEditorRoutingContext);

  if (context === undefined) {
    throw new Error(
      "useWorkflowsEditorRouting must be used within a WorkflowsEditorRoutingProvider",
    );
  }
  return context;
}
